import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Star, Trash2 } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import ProductForm from "./ProductForm";

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | product

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [{ data: products }, { data: cats }] = await Promise.all([
        api.get("/admin/products"),
        api.get("/admin/categories").catch(() => ({ data: [] })),
      ]);
      setItems(products);
      setCategories(cats);
    } catch {
      setError("Could not load products. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? It will disappear from the website immediately.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      setItems((list) => list.filter((x) => x.id !== p.id));
    } catch {
      setError("Could not delete the product. Please try again.");
    }
  };

  return (
    <section data-testid="admin-products">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-[#5C5C5C]">
          {items.length} product{items.length === 1 ? "" : "s"} — changes appear on the website instantly.
        </p>
        <button
          data-testid="add-product-button"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white"
        >
          <Plus size={16} /> Add Product
        </button>
      </div>

      {error && (
        <p data-testid="products-error" className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600">
          {error}
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 size={26} className="animate-spin text-rose" />
        </div>
      ) : items.length === 0 ? (
        <p data-testid="products-empty" className="rounded-2xl border border-line bg-white py-16 text-center text-sm text-[#5C5C5C]">
          No products yet. Click "Add Product" to create your first one.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <article
              key={p.id}
              data-testid={`admin-product-card-${p.id}`}
              className="overflow-hidden rounded-2xl border border-line bg-white"
            >
              <div className="relative">
                {p.image ? (
                  <img src={imgUrl(p.image)} alt={p.name} className="aspect-[4/3] w-full object-cover" />
                ) : (
                  <div className="flex aspect-[4/3] w-full items-center justify-center bg-cream text-xs text-[#5C5C5C]">
                    No image
                  </div>
                )}
                <div className="absolute left-3 top-3 flex gap-2">
                  {p.featured && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gold/90 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                      <Star size={10} className="fill-white" /> Featured
                    </span>
                  )}
                  {!p.active && (
                    <span className="rounded-full bg-ink/80 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                      Hidden
                    </span>
                  )}
                </div>
              </div>
              <div className="p-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-rose">{p.category}</p>
                <h3 className="mt-1 font-serif text-lg font-medium leading-snug">{p.name}</h3>
                <p className="mt-1 text-sm font-semibold">{p.price}</p>
                <div className="mt-4 flex gap-2">
                  <button
                    data-testid={`product-edit-${p.id}`}
                    onClick={() => setEditing(p)}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-ink px-3 py-2.5 text-xs font-medium transition-colors hover:bg-ink hover:text-cream"
                  >
                    <Pencil size={13} /> Edit
                  </button>
                  <button
                    data-testid={`product-delete-${p.id}`}
                    onClick={() => remove(p)}
                    aria-label={`Delete ${p.name}`}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-[#5C5C5C] transition-colors hover:border-red-400 hover:text-red-500"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing !== null && (
        <ProductForm
          product={editing === "new" ? null : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </section>
  );
}
